// apps/web/src/hooks/useAdminAlertsRealtime.ts
import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthProvider";
import { Admin } from "../lib/api";

/**
 * FAZ 16: Admin Alerts Realtime Hook
 * 
 * Backend SSE stream'ine bağlanır ve admin navbar'daki
 * alert badge'ini güncellemek için real-time alert sayısını takip eder.
 * 
 * Features:
 * - Initial count fetch from REST API
 * - SSE connection to /api/admin/realtime/notification-events
 * - Auto-reconnect on disconnect (exponential backoff)
 * - Alert count updates (alert_new, alert_update events)
 * - Only works for admin users
 */

type AlertStatus = "open" | "acknowledged" | "resolved";

interface SystemAlert {
  _id: string;
  type: string;
  severity: "HIGH" | "MEDIUM" | "LOW";
  message: string;
  status: AlertStatus;
  timestamp: string;
  [key: string]: any;
}

interface SSEMessage {
  type: "connected" | "alert_new" | "alert_update";
  data?: any;
}

interface UseAdminAlertsRealtimeResult {
  alertsCount: number; // Open + acknowledged alert count
  lastAlert: SystemAlert | null; // Most recent alert
  connected: boolean; // SSE connection status
  error: string | null; // Error message
  refetch: () => Promise<void>; // Manual refetch function
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:5000";
const SSE_ENDPOINT = `${API_BASE}/api/admin/realtime/notification-events`;
const INITIAL_RETRY_DELAY = 3000; // 3 seconds
const MAX_RETRY_DELAY = 30000; // 30 seconds

export function useAdminAlertsRealtime(): UseAdminAlertsRealtimeResult {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin === true;

  const [alertsCount, setAlertsCount] = useState(0);
  const [lastAlert, setLastAlert] = useState<SystemAlert | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const retryTimeoutRef = useRef<number | null>(null);
  const retryDelayRef = useRef(INITIAL_RETRY_DELAY);
  const mountedRef = useRef(true);

  /**
   * Fetch initial alert count from REST API
   */
  const fetchInitialCount = useCallback(async () => {
    if (!isAdmin) {
      setAlertsCount(0);
      return;
    }

    try {
      const response = await Admin.notificationListSystemAlerts({ limit: 200 });

      if (response?.ok && Array.isArray(response.alerts)) {
        // Count only open + acknowledged alerts
        const count = response.alerts.filter(
          (a: SystemAlert) => a.status === "open" || a.status === "acknowledged"
        ).length;

        setAlertsCount(count);
        setError(null);
      }
    } catch (err: any) {
      console.error("[useAdminAlertsRealtime] Failed to fetch initial count:", err);
      setError(err.message || "Failed to load alerts");
    }
  }, [isAdmin]);

  /**
   * Connect to SSE stream
   */
  const connectSSE = useCallback(() => {
    if (!isAdmin || !mountedRef.current) {
      return;
    }

    // Cleanup existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    try {
      const eventSource = new EventSource(SSE_ENDPOINT, {
        withCredentials: true,
      });

      eventSourceRef.current = eventSource;

      // Connection opened
      eventSource.onopen = () => {
        console.log("[useAdminAlertsRealtime] SSE connected");
        setConnected(true);
        setError(null);
        retryDelayRef.current = INITIAL_RETRY_DELAY; // Reset retry delay
      };

      // Message received
      eventSource.onmessage = (event) => {
        try {
          const message: SSEMessage = JSON.parse(event.data);

          switch (message.type) {
            case "connected": {
              console.log("[useAdminAlertsRealtime] SSE handshake received");
              break;
            }

            case "alert_new": {
              const alert: SystemAlert = message.data;
              
              // Increment count if alert is open or acknowledged
              if (alert.status === "open" || alert.status === "acknowledged") {
                setAlertsCount((prev) => prev + 1);
              }

              setLastAlert(alert);
              console.log("[useAdminAlertsRealtime] New alert received:", alert);
              break;
            }

            case "alert_update": {
              const alert: SystemAlert = message.data;
              
              // Update count based on status change
              // If alert changed to resolved, decrement count
              // This is a simplified approach; ideally we'd track previous status
              if (alert.status === "resolved") {
                setAlertsCount((prev) => Math.max(0, prev - 1));
              }

              setLastAlert(alert);
              console.log("[useAdminAlertsRealtime] Alert updated:", alert);
              break;
            }

            default:
              console.log("[useAdminAlertsRealtime] Unknown message type:", message.type);
          }
        } catch (err) {
          console.error("[useAdminAlertsRealtime] Failed to parse SSE message:", err);
        }
      };

      // Error occurred
      eventSource.onerror = (err) => {
        console.error("[useAdminAlertsRealtime] SSE error:", err);
        setConnected(false);
        setError("SSE connection lost");

        // Close and attempt reconnect
        eventSource.close();
        eventSourceRef.current = null;

        // Exponential backoff retry
        if (mountedRef.current) {
          const delay = retryDelayRef.current;
          retryDelayRef.current = Math.min(delay * 2, MAX_RETRY_DELAY);

          console.log(`[useAdminAlertsRealtime] Reconnecting in ${delay}ms...`);
          
          retryTimeoutRef.current = setTimeout(() => {
            connectSSE();
          }, delay);
        }
      };
    } catch (err: any) {
      console.error("[useAdminAlertsRealtime] Failed to create EventSource:", err);
      setError(err.message || "Failed to connect to SSE");
      setConnected(false);
    }
  }, [isAdmin]);

  /**
   * Manual refetch function
   */
  const refetch = useCallback(async () => {
    await fetchInitialCount();
  }, [fetchInitialCount]);

  /**
   * Initialize: Fetch count + connect SSE
   */
  useEffect(() => {
    if (!isAdmin) {
      setAlertsCount(0);
      setLastAlert(null);
      setConnected(false);
      setError(null);
      return;
    }

    // Fetch initial count
    fetchInitialCount();

    // Connect SSE (with small delay to avoid race condition)
    const timer = setTimeout(() => {
      connectSSE();
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [isAdmin, fetchInitialCount, connectSSE]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      // Cleanup SSE connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      // Cleanup retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []);

  return {
    alertsCount,
    lastAlert,
    connected,
    error,
    refetch,
  };
}
